import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    ApplyPipe,
    ArrayPipe,
    ButtonComponent,
    ChipChoiceComponent,
    DeltaViewComponent,
    FormControlValuePipe,
    FormFieldComponent,
    IconButtonComponent,
    ImageComponent,
    InnerEllipsesTextComponent,
    InputTextComponent,
    LoadMoreComponent,
    MenuComponent,
    MenuDecoratorDirective,
    MenuDialogComponent,
    MenuItemDecoratorDirective,
    SafeHtmlPipe,
    SegmentedButtonComponent,
    SeparatorComponent,
    ToggleIconButtonComponent,
} from '@interacta-shared/ui';
import { AIFeedbackComponent } from '@interacta-shared/ui-ai';
import {
    AiLoaderComponent,
    CreationDateComponent,
} from '@interacta-shared/ui-common';
import { IconComponent } from '@interacta-shared/ui-icons';
import { InteractaDatePipe } from '@interacta-shared/util-common';
import { AttachmentPreviewComponent } from '@interacta/feature-attachment';
import {
    BOTTOM_MENU_ITEM_IDS,
    BottomMenuService,
    componentsInclude,
} from '@interacta/feature-bottom-menu';
import { AttachmentsSelectionListComponent } from '@modules/attachments/components/attachments-selection-list/attachments-selection-list.component';
import { AppState } from '@modules/core/store';
import { MentionDeltaEditorComponent } from '@modules/shared-v2/components/mention-delta-editor/mention-delta-editor.component';
import { Actions, EffectsModule, ofType } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, filter, take } from 'rxjs';
import { SharedV2Module } from '../shared-v2/shared-v2.module';
import { AiContentGenerationModalComponent } from './components/ai-content-generation-modal/ai-content-generation-modal.component';
import {
    AIFabComponent,
    ANIMATION_DURATION_MS,
} from './components/ai-fab/ai-fab.component';
import { AIMessageAttachmentChipComponent } from './components/ai-message-attachment-chip/ai-message-attachment-chip.component';
import { AIMessageComponent } from './components/ai-message/ai-message.component';
import { AIPanelChatAttachmentButtonComponent } from './components/ai-panel-chat-attachment-button/ai-panel-chat-attachment-button.component';
import { AIPanelChatComponent } from './components/ai-panel-chat/ai-panel-chat.component';
import { AIPanelOverlayComponent } from './components/ai-panel-overlay/ai-panel-overlay.component';
import { AIPanelTipComponent } from './components/ai-panel-tip/ai-panel-tip.component';
import { AIPanelComponent } from './components/ai-panel/ai-panel.component';
import { AISkeletonTextComponent } from './components/ai-skeleton-text/ai-skeleton-text.component';
import { AIContextType } from './models/ai.model';
import { AIActions } from './store/ai.actions';
import { AIEffects } from './store/ai.effects';
import { AIReducer } from './store/ai.reducer';
import { selectIsPanelOpen } from './store/ai.selectors';

const components = [
    AIPanelChatAttachmentButtonComponent,
    AIPanelChatComponent,
    AIPanelComponent,
    AIPanelTipComponent,
    AIFabComponent,
    AIMessageAttachmentChipComponent,
    AIMessageComponent,
    AIPanelOverlayComponent,
];

const standaloneComponents = [
    AiContentGenerationModalComponent,
    AISkeletonTextComponent,
    AiLoaderComponent,
    ButtonComponent,
    ChipChoiceComponent,
    FormFieldComponent,
    ToggleIconButtonComponent,
    IconButtonComponent,
    IconComponent,
    InnerEllipsesTextComponent,
    ImageComponent,
    InputTextComponent,
    LoadMoreComponent,
    MenuComponent,
    SegmentedButtonComponent,
    SeparatorComponent,
    DeltaViewComponent,
    MenuDialogComponent,
    AttachmentsSelectionListComponent,
    AIFeedbackComponent,
    AttachmentPreviewComponent,
    CreationDateComponent,
    MentionDeltaEditorComponent,
];

const pipes = [
    ApplyPipe,
    ArrayPipe,
    SafeHtmlPipe,
    InteractaDatePipe,
    FormControlValuePipe,
];

const standaloneDirectives = [
    MenuDecoratorDirective,
    MenuItemDecoratorDirective,
];

@NgModule({
    declarations: [...components],
    imports: [
        CommonModule,
        DragDropModule,
        SharedV2Module,
        TranslateModule,
        MatTooltipModule,
        StoreModule.forFeature('ai', AIReducer),
        EffectsModule.forFeature([AIEffects]),
        ...standaloneComponents,
        ...standaloneDirectives,
        ...pipes,
    ],
    exports: [...components],
})
export class AIModule {
    constructor(
        private bottomMenuService: BottomMenuService,
        private store: Store<AppState>,
        private actions: Actions,
    ) {
        const isAIPanelOpen = this.store.selectSignal(selectIsPanelOpen);

        this.actions.pipe(ofType(AIActions.openGuide)).subscribe(() => {
            const components = this.bottomMenuService.getComponents();
            if (!componentsInclude(components, BOTTOM_MENU_ITEM_IDS.AI_FAB)) {
                this.bottomMenuService.addComponent({
                    id: BOTTOM_MENU_ITEM_IDS.AI_FAB,
                    type: AIFabComponent,
                    isOpen: isAIPanelOpen,
                });
            }

            this.store
                .select(selectIsPanelOpen)
                .pipe(
                    debounceTime(ANIMATION_DURATION_MS),
                    filter((value) => value === false),
                    take(1),
                )
                .subscribe(() => {
                    if (
                        !componentsInclude(
                            components,
                            BOTTOM_MENU_ITEM_IDS.AI_FAB,
                        )
                    ) {
                        this.bottomMenuService.removeComponent(AIFabComponent);
                    }
                });

            this.store.dispatch(
                AIActions.setActiveContext({
                    contextId: -1,
                    contextType: AIContextType.GUIDE,
                    workflowEnabled: false,
                    initialContentGeneration: null,
                }),
            );

            this.store.dispatch(AIActions.toggleAIPanel({ isOpen: true }));
        });
    }
}
